import React, { useState } from 'react';
import './AuthPage.css';
import { supabase } from './supabaseClient';
import { auth, provider } from './firebaseConfig';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  signInWithPopup,
} from 'firebase/auth';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);

  // ✅ Added: Password strength state (0-3)
  const [passwordStrength, setPasswordStrength] = useState(0);

  // ✅ Slight change: reset strength when switching forms
  const toggleAuthType = () => {
    setIsLogin((v) => !v);
    setPasswordStrength(0);
  };

  // ✅ Added: Simple strength scoring for 3 bars
  const checkPasswordStrength = (password) => {
    let score = 0;

    if (!password) {
      setPasswordStrength(0);
      return;
    }

    // Bar 1: length >= 8
    if (password.length >= 8) score++;

    // Bar 2: has letters + numbers (or uppercase + number)
    if (/[0-9]/.test(password) && /[A-Za-z]/.test(password)) score++;

    // Bar 3: has special char
    if (/[^A-Za-z0-9]/.test(password)) score++;

    setPasswordStrength(score);
  };

  // ✅ Added: handler for signup password input
  const handleSignUpPasswordChange = (e) => {
    checkPasswordStrength(e.target.value);
  };

  // Ensures a row exists in Supabase `users` for this Firebase user
  const ensureSupabaseUserRow = async (firebaseUser) => {
    // maybeSingle() avoids throwing when no row exists
    const { data: userDoc, error: selectError } = await supabase
      .from('users')
      .select('*')
      .eq('UID', firebaseUser.uid)
      .maybeSingle();

    if (selectError) {
      console.error('Supabase select error:', selectError);
      throw selectError;
    }

    if (!userDoc) {
      const { error: insertError } = await supabase.from('users').insert([
        {
          UID: firebaseUser.uid,
          membershipType: 'free',
        },
      ]);

      if (insertError) {
        console.error('Supabase insert error:', insertError);
        throw insertError;
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await ensureSupabaseUserRow(user);

      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error during Google Sign In', error);
      alert(error.message || 'Google sign-in failed.');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      if (isLogin) {
        const { user } = await signInWithEmailAndPassword(auth, email, password);

        if (!user.emailVerified) {
          await sendEmailVerification(auth.currentUser);
          alert('Verification email sent. Please verify your email before proceeding.');
          return;
        }

        // ✅ Ensure user exists in Supabase on login too
        await ensureSupabaseUserRow(user);

        window.location.href = '/dashboard';
      } else {
        const username = e.target.username.value;
        const name = e.target.name.value; // you had this field but weren’t using it

        const { user } = await createUserWithEmailAndPassword(auth, email, password);

        // Store displayName (optional)
        await updateProfile(user, { displayName: username });

        // ✅ Ensure user exists in Supabase on signup
        // You can store extra fields too if you have columns for them
        const { data: existing, error: selectError } = await supabase
          .from('users')
          .select('*')
          .eq('UID', user.uid)
          .maybeSingle();

        if (selectError) throw selectError;

        if (!existing) {
          const { error: insertError } = await supabase.from('users').insert([
            {
              UID: user.uid,
              membershipType: 'free',
              // Only include these if your table has these columns:
              // username,
              // name,
              // email,
            },
          ]);
          if (insertError) throw insertError;
        }

        await sendEmailVerification(auth.currentUser);
        alert('Account created. Verification email sent. Please verify your email before proceeding.');
      }
    } catch (error) {
      console.error('Auth error:', error);
      alert(error.message || 'Authentication failed.');
    }
  };

  // ✅ Added: derive label for UI
  const strengthLabel =
    passwordStrength === 0
      ? 'Very weak'
      : passwordStrength === 1
      ? 'Weak'
      : passwordStrength === 2
      ? 'Good'
      : 'Strong';

  // ✅ Added: bar color classes (matches your tailwind-ish style)
  // Uses your existing palette classes: red, text-lightPurple, purple, gray-600, etc.
  const barClass = (barIndex) => {
    const filled = passwordStrength >= barIndex;
    if (!filled) return 'bg-gray-600';
    if (passwordStrength === 0) return 'bg-red';
    if (passwordStrength === 1) return 'bg-red';
    if (passwordStrength === 2) return 'bg-yellow';
    if (passwordStrength === 3) return 'bg-green';
    return 'bg-purple';
  };

  return (
    <div className="bg-dark-main text-white-default min-h-screen font-sans flex flex-col">
      <header className="container mx-auto p-4 flex justify-between items-center">
        <h1 className="text-2xl font-extrabold">LogBug</h1>
      </header>

      <section className="container mx-auto flex flex-col items-center pb-5">
        <div className="lg:w-2/3 text-center p-4">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-3 leading-tight">
            {isLogin ? 'Welcome Back to LogBug' : 'Join LogBug Today'}
          </h1>
          <p className="text-md lg:text-lg text-gray-400 mb-4 leading-relaxed">
            {isLogin ? 'Please login to continue.' : 'Create an account to start your journey with us.'}
          </p>
        </div>

        <div className="w-full lg:w-1/2 bg-dark-100 p-4 rounded-lg shadow-md">
          <div className="auth-toggle flex justify-center mb-4">
            <button
              className={`px-3 py-1 rounded-l-md hover:bg-text-lightPurple ${
                isLogin ? 'text-white bg-purple' : 'bg-card-purple text-gray-400'
              }`}
              onClick={toggleAuthType}
              type="button"
            >
              Login
            </button>
            <button
              className={`px-3 py-1 rounded-r-md hover:bg-text-lightPurple ${
                !isLogin ? 'text-white bg-purple' : 'bg-card-purple text-gray-400'
              }`}
              onClick={toggleAuthType}
              type="button"
            >
              Sign Up
            </button>
          </div>

          {isLogin ? (
            <div>
              <h2 className="text-xl font-bold mb-3">Member Login</h2>
              <form onSubmit={handleAuth} key="login">
                <label htmlFor="email" className="block text-gray-400 mb-1">
                  Email:
                </label>
                <input
                  type="email"
                  className="text-dark-100 mb-3 w-full p-2 rounded-md"
                  id="email"
                  name="email"
                  required
                />

                <label htmlFor="password" className="block text-gray-400 mb-1">
                  Password:
                </label>
                <input
                  type="password"
                  className="text-dark-100 mb-3 w-full p-2 rounded-md"
                  id="password"
                  name="password"
                  required
                />

                <button className="px-6 py-2 bg-purple text-white rounded-md hover:bg-card-purple ease-in-out transition focus:outline-none focus:ring-2 focus:ring-primary">
                  Login
                </button>
              </form>

              <hr className="break-bar my-8" />

              <div className="google-sign-in">
                <button
                  onClick={handleGoogleSignIn}
                  className="px-4 py-2 bg-red text-white hover:bg-dark-red transition rounded-md w-full"
                  type="button"
                >
                  Sign In with Google
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-bold mb-3">Sign Up</h2>
              <form onSubmit={handleAuth} key="signup">
                <label htmlFor="username" className="block text-gray-400 mb-1">
                  Username:
                </label>
                <input
                  type="text"
                  className="flex-1 px-4 py-2 border border-gray-600 bg-dark-main text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-3 w-full"
                  id="username"
                  name="username"
                  required
                />

                <label htmlFor="name" className="block text-gray-400 mb-1">
                  Name:
                </label>
                <input
                  type="text"
                  className="flex-1 px-4 py-2 border border-gray-600 bg-dark-main text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-3 w-full"
                  id="name"
                  name="name"
                  required
                />

                <label htmlFor="emailSignUp" className="block text-gray-400 mb-1">
                  Email:
                </label>
                <input
                  type="email"
                  className="flex-1 px-4 py-2 border border-gray-600 bg-dark-main text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-3 w-full"
                  id="emailSignUp"
                  name="email"
                  required
                />

                <label htmlFor="passwordSignUp" className="block text-gray-400 mb-1">
                  Password:
                </label>
                <input
                  type="password"
                  className="flex-1 mb-2 px-4 py-2 border border-gray-600 bg-dark-main text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-full"
                  id="passwordSignUp"
                  name="password"
                  required
                  onChange={handleSignUpPasswordChange}
                />

                {/* ✅ Added: 3-bar password strength UI */}
                <div className="w-full mb-4">
                  <div className="flex gap-2">
                  <div className={`h-2 w-1/3 rounded ${barClass(0)}`} />
                    <div className={`h-2 w-1/3 rounded ${barClass(1)}`} />
                    <div className={`h-2 w-1/3 rounded ${barClass(2)}`} />
                    <div className={`h-2 w-1/3 rounded ${barClass(3)}`} />
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Password strength: <span className="text-white">{strengthLabel}</span>
                  </p>
                </div>

                <button className="px-6 py-2 bg-purple text-white rounded-md hover:bg-card-purple ease-in-out transition focus:outline-none focus:ring-2 focus:ring-primary">
                  Sign Up
                </button>
              </form>

              <hr className="break-bar my-8" />

              <div className="google-sign-in">
                <button
                  onClick={handleGoogleSignIn}
                  className="px-4 py-2 bg-red text-white hover:bg-dark-red transition rounded-md w-full"
                  type="button"
                >
                  Sign Up with Google
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="py-6 bg-dark-200 text-center">
        <p className="text-gray-500">&copy; 2024 LogBug. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AuthPage;
